import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import type { WireSourceInstance } from '@logistics/contracts';
import { failureOf } from '@/lib/result';
import { api } from '../client';
import { useToast } from '../shell/toast';
import { useRefreshSources } from './use-sources';

/** A refusal in a sentence: the server's own reason, or its status. */
function refusal(result: { status: number; body: unknown }): string {
  return failureOf(result).detail ?? `the API answered ${result.status}`;
}

/**
 * What the side panel can do to one source: test it, pause or resume it, and
 * delete it. Each ends in a toast; each refreshes the list and its health.
 */
export function useSourceActions(
  instance: WireSourceInstance,
  onDeleted: () => void,
): {
  test: UseMutationResult<void, Error, void>;
  toggle: UseMutationResult<void, Error, void>;
  remove: UseMutationResult<void, Error, void>;
} {
  const toast = useToast();
  const refresh = useRefreshSources();
  const params = { id: instance.id };
  const name = instance.label;

  const test = useMutation<void>({
    mutationFn: async () => {
      const r = await api.instances.test({ params, body: {} });
      if (r.status !== 200) return toast(`${name}: ${refusal(r)}`);
      toast(
        r.body.ok
          ? `${name}: connection OK (${r.body.latencyMs} ms)`
          : `${name}: still failing: ${r.body.error ?? 'no reason given'}`,
      );
      await refresh();
    },
  });

  const toggle = useMutation<void>({
    mutationFn: async () => {
      const r = await api.instances.toggle({ params, body: {} });
      if (r.status !== 200) return toast(`${name}: ${refusal(r)}`);
      toast(r.body.instance.enabled ? `${name} resumed` : `${name} paused. Credentials kept.`);
      await refresh();
    },
  });

  const remove = useMutation<void>({
    mutationFn: async () => {
      const r = await api.instances.remove({ params, body: {} });
      if (r.status !== 200) return toast(`${name}: ${refusal(r)}`);
      toast(`${name} deleted, credentials wiped`);
      onDeleted();
      await refresh();
    },
  });

  return { test, toggle, remove };
}
